import { useQuery } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Skeleton,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  AccountDetailsGQLType,
  GetOrganizationsByIdsQueryGQLType,
  GetOrganizationsByIdsQueryVariablesGQLType,
  LedgerTransactionGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import {
  CashBalanceRow,
  cashBalancesFeed,
} from './temp-monthly-summary-demo-data';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { GET_ORGANIZATIONS_BY_IDS } from 'packages/organization/requests/organization.gql';
import {
  useGetLedgerAccountsQuery,
  useGetLedgerQuery,
} from 'packages/statements/requests/ledger.gql.generated';
import {
  MonthlySummaryTable,
  MonthSummary,
} from 'packages/statements/ui/MonthlySummaryTable';
import { MonthlySummaryTableHeader } from 'packages/statements/ui/MonthlySummaryTableHeader';

// year-month, account structure here operates under assumption that fee is paid to a single party and that the party never changes
// this can be more flexible by aggregating by org as well, but this is just demo code
type TransactionSummary = {
  [month: Util.FullDateStr]: MonthSummary;
};

const currentPeriod = commonDate.firstOfPeriod(
  commonDate.currentDateStr(),
  Util.FrequencyPeriod.Month,
);

const getTransactionSummary = (
  ledger?: LedgerTransactionGQLType[],
): TransactionSummary =>
  (ledger || []).reduce<TransactionSummary>((result, entry) => {
    const {
      postingDate,
      accountType: account,
      amount,
      transactionType,
      // type,
    } = entry;
    const firstOfMonth = commonDate.firstOfPeriod(
      postingDate,
      Util.FrequencyPeriod.Month,
    );

    // Early return if conditions do not match for accrual
    if (transactionType !== 'dailyAccrual') return result;
    // Early return if processing transactions for current month - don't show until closed
    if (firstOfMonth === currentPeriod) return result;

    if (result[firstOfMonth]) {
      if (result[firstOfMonth][account]) {
        // eslint-disable-next-line no-param-reassign
        result[firstOfMonth][account] += amount;
      } else {
        // eslint-disable-next-line no-param-reassign
        result[firstOfMonth][account] = amount;
      }
    } else {
      // eslint-disable-next-line no-param-reassign
      result[firstOfMonth] = {
        [account]: amount,
      };
    }

    return result;
  }, {});

const formatOptions = (
  values: Util.FullDateStr[],
): { label: string; value: Util.FullDateStr }[] =>
  values
    .sort()
    .slice(values.length - 6, values.length) // Last 6 months
    .map((value) => ({
      label: commonDate.fullDateStrToStr(value, 'MMMM yyyy'),
      value,
    }));

export const MonthlySummaryTableContainer = ({
  accounts,
  creditFacilityId,
}: {
  accounts: AccountDetailsGQLType[];
  creditFacilityId: string;
}) => {
  const toast = useToast();
  // TODO: check if current month should be excluded, default to prior month instead
  const priorMonth = commonDate.addMonths(commonDate.currentDateStr(), -1);
  const [period, setPeriod] = useState<Util.FullDateStr>(
    commonDate.firstOfPeriod(priorMonth, Util.FrequencyPeriod.Month),
  );
  const allAccountTypes = _.uniq(_.map(accounts, 'tAccount'));
  const allCountersignerIds = _.uniq(_.map(accounts, 'organizationId'));

  const {
    data: statementsData,
    loading: statementsLoading,
  } = useGetLedgerQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      organizationIds: allCountersignerIds,
      accountTypes: allAccountTypes,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { data: countersignersData, loading: countersignersLoading } = useQuery<
    GetOrganizationsByIdsQueryGQLType,
    GetOrganizationsByIdsQueryVariablesGQLType
  >(GET_ORGANIZATIONS_BY_IDS, {
    variables: {
      organizationIds: allCountersignerIds,
    },
  });

  const statementsDataIsLoading =
    statementsLoading ||
    !statementsData ||
    countersignersLoading ||
    !countersignersData;

  const transactionSummary = getTransactionSummary(statementsData?.ledger);
  const periods = Object.keys(transactionSummary) as Util.FullDateStr[];

  let monthSummary = {};
  if (transactionSummary[period]) monthSummary = transactionSummary[period];

  let cashBalances: CashBalanceRow[] = [];
  if (cashBalancesFeed[period]) cashBalances = cashBalancesFeed[period];
  // If performance is an issue,set up useEffect
  // to perform retrieval of statement data for
  // one month at a time using the select dropdown.
  // Feed handler into onChangeHandler for table

  let ledgerData: LedgerTransactionGQLType[] = [];
  if (statementsData?.ledger) {
    ledgerData = _.sortBy(
      _.filter(
        statementsData.ledger,
        (entry) =>
          commonDate.firstOfPeriod(
            entry.postingDate,
            Util.FrequencyPeriod.Month,
          ) === period,
      ),
      'postingDate',
    );
  }

  return (
    <Box>
      {statementsDataIsLoading ? (
        <Skeleton data-testid="loading-ledger" height="600px" />
      ) : (
        <>
          <MonthlySummaryTableHeader
            isLoading={statementsDataIsLoading}
            periods={formatOptions(periods)}
            selectedPeriod={period}
            ledgerData={ledgerData}
            onChangeHandler={(value: Util.FullDateStr) => setPeriod(value)}
          />
          <Box height="30px" />

          <MonthlySummaryTable
            monthFormatted={formatOptions([period])[0].label}
            monthSummary={monthSummary}
            cashBalances={cashBalances}
          />
        </>
      )}
    </Box>
  );
};

type MonthlySummaryViewContainerProps = { creditFacilityId: string };
export const MonthlySummaryViewContainer = ({
  creditFacilityId,
}: MonthlySummaryViewContainerProps) => {
  const toast = useToast();
  const {
    data: ledgerAccountsData,
    loading: ledgerAccountsLoading,
  } = useGetLedgerAccountsQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      ledgerAccountGroup: 'expensesFees',
    },
  });

  if (ledgerAccountsLoading || !ledgerAccountsData) {
    return <></>;
  }

  if (ledgerAccountsData.ledgerAccounts.length === 0)
    return (
      <Alert status="info" mt="20px">
        <AlertIcon />
        <AlertDescription>
          No accounts configured. Please contact support for facility setup.
        </AlertDescription>
      </Alert>
    );

  return (
    <Box>
      <MonthlySummaryTableContainer
        accounts={ledgerAccountsData.ledgerAccounts}
        creditFacilityId={creditFacilityId}
      />
    </Box>
  );
};
